import { isEmpty, isNumber, map } from 'underscore';

import { AllMetricNames } from '../enums/metrics';
import { Location } from '../enums/location';
import { TrendPeriodLowercase } from '../enums/trend-period';
import { Vertical } from '../enums/vertical';

export const formatMetricName = (i18n, metric) => {
  const key = AllMetricNames[metric];
  return key ? i18n.getString(key) : metric;
};

export const formatLocation = (i18n, location) => {
  const key = Location[location];
  return key ? i18n.getString(key) : location;
};

export const formatVertical = (i18n, vertical) => {
  const key = Vertical[vertical];
  return key ? i18n.getString(key) : vertical;
};

export const formatDropdownOptions = (i18n, options) => map(options, option => ({
  ...option,
  text: i18n.getString(option.text),
}));

export const formatInsightDetails = (i18n, details, showTrendPeriod = true) => {
  if (isEmpty(details)) {
    return '';
  }

  const {
    Country,
    TrendPeriod,
  } = details;

  if (showTrendPeriod) {
    return i18n.getString(_TL_('{{country}} - {{trendPeriod}} trend'), {
      country: formatLocation(i18n, Country),
      trendPeriod: i18n.getString(TrendPeriodLowercase[TrendPeriod]),
    });
  }

  return formatLocation(i18n, Country);
};

const formatZeroNumberWithSign = (i18n) => {
  const cldr = i18n.getCldr();
  const plusSign = cldr.main('numbers/symbols-numberSystem-latn/plusSign');

  return `${plusSign}${i18n.formatDecimalToInteger(0)}`;
};

const formatZeroPercentWithSign = (i18n) => {
  const cldr = i18n.getCldr();
  const plusSign = cldr.main('numbers/symbols-numberSystem-latn/plusSign');

  return `${plusSign}${i18n.formatPercent(0)}`;
};

const formatZeroCurrencyWithSign = (i18n, currency) => {
  const cldr = i18n.getCldr();
  const plusSign = cldr.main('numbers/symbols-numberSystem-latn/plusSign');

  return `${plusSign}${i18n.formatCurrency(0, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    withSign: 'never',
  })}`;
};

export const formatCurrency = (i18n, value, withSign = false, currency) => {
  if (!isNumber(value)) {
    return '';
  }
  if (withSign && value.toFixed(2) == 0) { // eslint-disable-line eqeqeq
    return formatZeroCurrencyWithSign(i18n, currency);
  }
  return i18n.formatCurrency(value, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    withSign: withSign ? 'always' : 'auto',
  });
};

export const formatDecimal = (i18n, value, withSign = false) => {
  if (!isNumber(value)) {
    return '';
  }
  if (withSign && value.toFixed(2) == 0) { // eslint-disable-line eqeqeq
    return formatZeroNumberWithSign(i18n);
  }
  return i18n.formatDecimal(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    withSign: withSign ? 'always' : 'auto',
  });
};

export const formatInteger = (i18n, value, withSign = false) => {
  if (!isNumber(value)) {
    return '';
  }
  if (withSign && value.toFixed(2) == 0) { // eslint-disable-line eqeqeq
    return formatZeroNumberWithSign(i18n);
  }
  return i18n.formatDecimalToInteger(value, {
    withSign: withSign ? 'always' : 'auto',
  });
};

export const formatMetricDelta = (i18n, value, categorized = false) => {
  if (categorized) {
    if (!isNumber(value)) {
      return '';
    }
    if (value < 10000) {
      return i18n.getString(_TL_('+0-10K searches'));
    }
    if (value < 100000) {
      return i18n.getString(_TL_('+10K-100K searches'));
    }
    if (value < 1000000) {
      return i18n.getString(_TL_('+100K-1M searches'));
    }
    if (value < 10000000) {
      return i18n.getString(_TL_('+1M-10M searches'));
    }
    return i18n.getString(_TL_('+10M+ searches'));
  }

  return formatDecimal(i18n, value, true);
};

export const formatMetricPercent = (i18n, value, withSign = true) => {
  if (!isNumber(value)) {
    return '';
  }
  if (withSign && value.toFixed(2) == 0) { // eslint-disable-line eqeqeq
    return formatZeroPercentWithSign(i18n);
  }
  return i18n.formatPercent(value, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    withSign: withSign ? 'always' : 'auto',
  });
};

export const formatChangeRatio = (i18n, value, withSign = true) => {
  if (!isNumber(value)) {
    return '';
  }
  if (!withSign && value < 0.05) {
    return i18n.getString(_TL_('<5%'));
  }
  if (value > 10) {
    return i18n.getString(_TL_('>1000%'));
  }
  return formatMetricPercent(i18n, value, withSign);
};

export const formatSearchInterest = (i18n, value) => {
  if (!isNumber(value)) {
    return '';
  }
  if (value === 0) {
    return i18n.formatDecimal(value); // 0 has 0 significant digit
  }
  if (value < 1000) {
    return i18n.formatDecimal(value, {
      minimumSignificantDigits: 1,
      maximumSignificantDigits: 1,
      round: 'truncate',
    }); // e.g. 500
  }
  if (value < 1000000) {
    return i18n.getString(_TL_('{{number}}K'), {
      number: i18n.formatDecimal(Math.trunc(value / 1000), {
        minimumSignificantDigits: 1,
        maximumSignificantDigits: 1,
        round: 'truncate',
      }),
    }); // e.g. 500K
  }
  if (value < 1000000000) {
    return i18n.getString(_TL_('{{number}}M'), {
      number: i18n.formatDecimal(Math.trunc(value / 1000000), {
        minimumSignificantDigits: 1,
        maximumSignificantDigits: 1,
        round: 'truncate',
      }),
    }); // e.g. 500M
  }
  return i18n.getString(_TL_('{{number}}B'), {
    number: i18n.formatDecimal(Math.trunc(value / 1000000000), {
      minimumSignificantDigits: 1,
      maximumSignificantDigits: 1,
      round: 'truncate',
    }),
  }); // e.g. 500B
};
